/**
 * Vertical swipe, for the things that open and close by sliding.
 *
 * A phone user reaches for a drag before they look for a chevron, and on a
 * counter — one hand holding a tray, the other on the screen — a gesture that
 * works anywhere on a panel beats a target the size of a fingernail.
 *
 * Pointer events rather than touch events, so the same code answers a finger, a
 * stylus and a mouse; `setPointerCapture` keeps the gesture alive when the
 * pointer leaves the element mid-drag, which is exactly what a fast flick does.
 *
 * A mouse counts too. Restricting this to touch meant the gesture could not be
 * tried in a desktop browser at all — including by the person who asked for it —
 * and the guards that make it safe (a 40px threshold, vertical dominance, and
 * ignoring anything begun on a control) do not depend on what is doing the
 * pointing.
 */

import { useRef, type PointerEvent as ReactPointerEvent } from 'react';

/** Travel before a drag counts, in CSS pixels. Below this it is a tap. */
const THRESHOLD_PX = 40;

/**
 * Speed that counts regardless of distance, in pixels per millisecond.
 *
 * Without this a quick flick fails: it covers little ground before the finger
 * lifts, and distance alone would read it as a tap. 0.5 px/ms is a deliberate
 * flick and not a slow drag that changed its mind.
 */
const FLICK_VELOCITY = 0.5;

export interface SwipeStart {
  x: number;
  y: number;
}

/**
 * Decide what a completed drag was.
 *
 * Pulled out of the hook and kept pure so the thresholds can be tested without
 * a browser: the awkward cases here are a flick that barely moves, a diagonal
 * that is really a scroll, and the boundary itself — none of which are
 * comfortable to reproduce by hand on a phone.
 */
export function classifySwipe(
  from: SwipeStart,
  to: SwipeStart,
  elapsedMs: number,
): 'up' | 'down' | 'none' {
  const dy = to.y - from.y;
  const dx = to.x - from.x;

  // Mostly-vertical only: a horizontal drag is a scroll or a text selection,
  // and treating it as a swipe makes the panel feel twitchy.
  if (Math.abs(dy) <= Math.abs(dx)) return 'none';

  const elapsed = Math.max(elapsedMs, 1);
  const travelled = Math.abs(dy) >= THRESHOLD_PX;
  const flicked = Math.abs(dy) / elapsed >= FLICK_VELOCITY && Math.abs(dy) > 12;
  if (!travelled && !flicked) return 'none';

  return dy < 0 ? 'up' : 'down';
}

export interface SwipeHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: (event: ReactPointerEvent) => void;
}

export interface VerticalSwipeOptions {
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

/** The bits of a pointer event this needs. Narrowed so tests can supply one. */
export interface SwipeEvent {
  clientX: number;
  clientY: number;
  pointerId: number;
  target: { closest(selector: string): unknown } | null;
  currentTarget: { setPointerCapture?(pointerId: number): void } | null;
}

/**
 * The gesture tracker, outside React.
 *
 * Separated from the hook so the whole handler — not just the threshold maths —
 * can be tested. That distinction is not academic: the first version of this
 * described pointer capture in its comment and never called it, so every swipe
 * that ended past the edge of the panel did nothing, and a test of the pure
 * classifier passed happily throughout.
 */
export function createSwipeTracker({ onSwipeUp, onSwipeDown }: VerticalSwipeOptions) {
  let start: { x: number; y: number; at: number } | null = null;

  return {
    down(event: SwipeEvent, now = Date.now()) {
      if (event.target?.closest('button, a, input, select, textarea')) {
        start = null;
        return;
      }
      start = { x: event.clientX, y: event.clientY, at: now };
      event.currentTarget?.setPointerCapture?.(event.pointerId);
    },

    up(event: SwipeEvent, now = Date.now()) {
      const from = start;
      start = null;
      if (!from) return;

      const direction = classifySwipe(from, { x: event.clientX, y: event.clientY }, now - from.at);
      if (direction === 'up') onSwipeUp?.();
      else if (direction === 'down') onSwipeDown?.();
    },

    cancel() {
      start = null;
    },

    /** For tests: is a gesture currently being tracked? */
    get tracking() {
      return start !== null;
    },
  };
}

/**
 * React binding. Deliberately a thin adapter over `createSwipeTracker` — if the
 * hook reimplemented the handling, the tests above it would be testing a copy
 * rather than the code that ships.
 */
export function useVerticalSwipe(options: VerticalSwipeOptions): SwipeHandlers {
  // Held in a ref and read at call time, so a tracker created once still calls
  // this render's callbacks rather than the ones from first mount.
  const latest = useRef(options);
  latest.current = options;

  const tracker = useRef<ReturnType<typeof createSwipeTracker> | null>(null);
  tracker.current ??= createSwipeTracker({
    onSwipeUp: () => latest.current.onSwipeUp?.(),
    onSwipeDown: () => latest.current.onSwipeDown?.(),
  });

  return {
    onPointerDown: (event) => tracker.current?.down(event as unknown as SwipeEvent),
    onPointerUp: (event) => tracker.current?.up(event as unknown as SwipeEvent),
    onPointerCancel: () => tracker.current?.cancel(),
  };
}

/**
 * Swipe down to dismiss, for panels that only close.
 *
 * Deliberately down-only. Up would be the gesture to make a dialog taller, and
 * these do not grow — binding it to "close" as well would mean a panel that
 * vanishes whichever way you pull it.
 */
export function useSwipeToDismiss(onDismiss: () => void): SwipeHandlers {
  return useVerticalSwipe({ onSwipeDown: onDismiss });
}
